import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, DollarSign, Palette, HelpCircle, Eye, RefreshCw, Users, ArrowRight, FileText } from "lucide-react";
import { injectUserStyles } from "../../userStyles";

export default function InvitedDashboard() {
  const [loading, setLoading]   = useState(true);
  const [projects, setProjects] = useState({ total: 0, shared: 0 });
  const [hostOrg, setHostOrg]   = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => { injectUserStyles(); }, []);

  async function load() {
    setLoading(true);
    try {
      const pr = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, { credentials: "include" });
      if (pr.ok) {
        const d = await pr.json();
        const items = d.items || [];
        setProjects({ total: items.length, shared: items.length });
      }
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/org/me`, { credentials: "include" });
        if (r.ok) { const d = await r.json(); setHostOrg(d?.organization || d?.org || null); }
      } catch {}
      setLastUpdated(new Date());
    } catch {
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const KPIS = [
    { accent: "#6366f1", val: projects.shared, lbl: "Shared Projects", sub: "Accessible to you" },
    { accent: "#0c1118", val: projects.total,  lbl: "Total Visible",   sub: "All projects"      },
    { accent: "#1d9e75", val: "View",           lbl: "Pricing Access",  sub: "Owner-controlled"  },
  ];

  const QUICK_LINKS = [
    { to: "/dashboard/projects",     icon: FolderOpen, name: "Projects",       desc: "Open shared projects"   },
    { to: "/dashboard/myquotes",     icon: DollarSign, name: "My Quotes",      desc: "View your quotes"       },
    { to: "/dashboard/engineering-plans", icon: FileText, name: "Eng. Plans",  desc: "Download plan PDFs"     },
    { to: "/dashboard/support",      icon: HelpCircle, name: "Support",        desc: "Get assistance"         },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <div className="up-title">Dashboard</div>
          <div className="up-sub">
            {hostOrg
              ? `Collaborating with ${hostOrg.name || hostOrg}`
              : "Collaborating on shared projects"}
            {lastUpdated && ` · Updated ${lastUpdated.toLocaleTimeString()}`}
          </div>
        </div>
        <button className="up-btn up-btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* Host org banner */}
      <div className="up-panel" style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "16px 20px" }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, background: "rgba(99,102,241,.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Users size={18} style={{ color: "#6366f1" }} />
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#0c1118" }}>
              {hostOrg ? `Member of ${hostOrg.name || hostOrg}` : "Collaborator Access"}
            </div>
            <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>
              You have access to shared projects and resources as permitted by the owner.
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="up-kpi-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        {KPIS.map((k) => (
          <div className="up-kpi" key={k.lbl}>
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val">{loading ? "—" : k.val}</div>
            <div className="up-kpi-lbl">{k.lbl}</div>
            <div className="up-kpi-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Quick links */}
      <div className="up-panel">
        <div className="up-panel-head">
          <div className="up-panel-title">What would you like to do?</div>
        </div>
        {QUICK_LINKS.map((a) => (
          <Link to={a.to} className="up-qa-link" key={a.to}>
            <div className="up-qa-icon"><a.icon size={16} /></div>
            <div style={{ flex: 1 }}>
              <div className="up-qa-name">{a.name}</div>
              <div className="up-qa-desc">{a.desc}</div>
            </div>
            <ArrowRight size={15} className="up-qa-arrow" />
          </Link>
        ))}
      </div>
    </div>
  );
}
